import type { FC } from 'react';
import React, { memo } from 'react';
import cn from 'classnames';

import type { TRadio, TStyles } from './models';

export const Radio: FC<React.PropsWithChildren<TRadio>> = memo(
    ({
        checked,
        children,
        dataTestId = 'radio',
        defaultChecked,
        disabled,
        forwardedRef,
        name,
        onBlur,
        onChange,
        onFocus,
        size = 's',
        styles,
        tabIndex,
        theme = 'white',
        value,
    }) => {
        const classNames = cn(
            styles.radio,
            styles[`${size}Size` as keyof TStyles],
            styles[`${theme}Theme` as keyof TStyles],
            {
                [styles.disabled]: disabled,
                [styles.checked]: checked && !disabled,
            }
        );

        const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (onChange) {
                onChange(event);
            }
        };

        return (
            <label className={classNames} data-test-id={dataTestId} tabIndex={tabIndex}>
                <input
                    ref={forwardedRef}
                    checked={checked}
                    defaultChecked={defaultChecked}
                    disabled={disabled}
                    name={name}
                    onBlur={onBlur}
                    onChange={handleOnChange}
                    onFocus={onFocus}
                    type="radio"
                    value={value}
                />

                <div className={styles.radioBlock}>
                    <div className={styles.control} />
                    <div className={styles.label}>{children}</div>
                </div>
            </label>
        );
    }
);

Radio.displayName = 'Radio';
